import React from 'react';

interface Testimonial {
  name: string;
  role: string;
  company: string;
  content: string;
  avatar: string;
  rating: number;
}

const SocialProofSection: React.FC = () => {
  const testimonials: Testimonial[] = [
    {
      name: 'Sarah Mitchell',
      role: 'Founder & CEO',
      company: 'E-commerce Business',
      content: 'Explanatum replaced our entire software stack. We went from paying £90/month for multiple tools to just £19. The AI Consultant alone is worth 10x the price.',
      avatar: '👩‍💼',
      rating: 5,
    },
    {
      name: 'James Rodriguez',
      role: 'Operations Director',
      company: 'Marketing Agency',
      content: 'The integration between modules is game-changing. Our CRM talks to our email campaigns, which feed into our project management. Finally, no more manual data entry.',
      avatar: '👨‍💻',
      rating: 5,
    },
    {
      name: 'Emma Thompson',
      role: 'Marketing Manager',
      company: 'Digital Consultancy',
      content: 'We were skeptical at first, but after the 7-day trial, we couldn\'t go back. The SEO tools, social media planner, and content hub save us 15 hours every week.',
      avatar: '👩‍🎨',
      rating: 5,
    },
  ];

  const trustBadges = [
    { icon: '🔒', label: 'Bank-level security' },
    { icon: '✅', label: 'GDPR compliant' },
    { icon: '⚡', label: '99.9% uptime' },
    { icon: '🛡️', label: 'SOC 2 certified' },
  ];

  return (
    <section className="py-20 bg-white relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 mb-4 text-sm font-semibold text-blue-600 bg-blue-50 rounded-full">
            Trusted by Thousands
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
            Join 2,000+ Growing Businesses
          </h2>
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <svg
                  key={i}
                  className="w-6 h-6 text-yellow-400 fill-current"
                  viewBox="0 0 20 20"
                >
                  <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                </svg>
              ))}
            </div>
            <span className="text-gray-600 font-medium">4.9/5 from 500+ reviews</span>
          </div>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {testimonials.map((testimonial, index) => (
            <div
              key={testimonial.name}
              className="bg-gradient-to-br from-white to-blue-50 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-blue-100 hover:-translate-y-2"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Rating Stars */}
              <div className="flex mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <svg
                    key={i}
                    className="w-5 h-5 text-yellow-400 fill-current"
                    viewBox="0 0 20 20"
                  >
                    <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                  </svg>
                ))}
              </div>

              {/* Testimonial Content */}
              <p className="text-gray-700 mb-6 leading-relaxed italic">
                "{testimonial.content}"
              </p>

              {/* Author Info */}
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-2xl">
                  {testimonial.avatar}
                </div>
                <div>
                  <p className="font-bold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-600">
                    {testimonial.role}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Trust Badges */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-2xl p-8 border-2 border-blue-100">
            <h3 className="text-center text-xl font-bold text-gray-900 mb-6">
              Enterprise-Grade Security & Reliability
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {trustBadges.map((badge) => (
                <div key={badge.label} className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-xl bg-white shadow-md flex items-center justify-center text-3xl mb-3">
                    {badge.icon}
                  </div>
                  <p className="text-sm font-semibold text-gray-700">{badge.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {[
            { number: '2,000+', label: 'Active businesses' },
            { number: '50,000+', label: 'AI tasks completed' },
            { number: '£2M+', label: 'Saved in software costs' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-1">
                {stat.number}
              </div>
              <p className="text-gray-600 font-medium">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SocialProofSection;
